/**
 * View Builder Context
 *
 * Provides state management for the 5-step view builder using the
 * Context + Reducer pattern.
 */

import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  ReactNode,
  useEffect,
  useRef,
} from "react";
import { v4 as uuidv4 } from "uuid";
import type {
  ViewBuilderState,
  ViewBuilderJoin,
  ViewBuilderField,
  ViewBuilderFilter,
  ViewBuilderPermission,
  SchemaTableWithColumns,
  SchemaRelationshipResponse,
  SchemaColumnResponse,
  JoinType,
  FilterOperator,
} from "../../types";

// ============================================================================
// localStorage Persistence
// ============================================================================

const STORAGE_KEY = "viewBuilder_draft";
const SAVE_DEBOUNCE_MS = 1000;

interface PersistedState {
  name: string;
  description: string;
  focusTableId: string | null;
  currentStep: number;
  // Full state persistence for better draft recovery
  isTemplate: boolean;
  isPublic: boolean;
  selectedJoins: ViewBuilderJoin[];
  selectedFields: ViewBuilderField[];
  filters: ViewBuilderFilter[];
  permissions: ViewBuilderPermission[];
}

function saveToStorage(state: ViewBuilderState): void {
  try {
    // Only save if there's actual content to preserve
    if (!state.name && !state.focusTableId) return;

    const persistedState: PersistedState = {
      name: state.name,
      description: state.description,
      focusTableId: state.focusTableId,
      currentStep: state.currentStep,
      isTemplate: state.isTemplate,
      isPublic: state.isPublic,
      selectedJoins: state.selectedJoins,
      selectedFields: state.selectedFields,
      filters: state.filters,
      permissions: state.permissions,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(persistedState));
  } catch (e) {
    console.warn("Failed to save view builder state to localStorage:", e);
  }
}

function loadFromStorage(): PersistedState | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as PersistedState;
  } catch (e) {
    console.warn("Failed to load view builder state from localStorage:", e);
    return null;
  }
}

function clearStorage(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    console.warn("Failed to clear view builder state from localStorage:", e);
  }
}

// ============================================================================
// Actions
// ============================================================================

type ViewBuilderAction =
  // Loading / Reset
  | { type: "LOAD_VIEW"; payload: Partial<ViewBuilderState> }
  | { type: "LOAD_RELATIONSHIPS"; payload: SchemaRelationshipResponse[] }
  | { type: "RESET" }
  // Step 1: Focus Table
  | { type: "SET_NAME"; payload: string }
  | { type: "SET_DESCRIPTION"; payload: string }
  | {
      type: "SET_FOCUS_TABLE";
      payload: { tableId: string; table: SchemaTableWithColumns };
    }
  | { type: "UPDATE_FOCUS_TABLE_COLUMNS"; payload: SchemaColumnResponse[] }
  | { type: "CLEAR_FOCUS_TABLE" }
  | { type: "SET_IS_TEMPLATE"; payload: boolean }
  | { type: "SET_IS_PUBLIC"; payload: boolean }
  // Step 2: Joins
  | { type: "ADD_JOIN"; payload: ViewBuilderJoin }
  | {
      type: "UPDATE_JOIN";
      payload: { joinId: string; updates: Partial<ViewBuilderJoin> };
    }
  | { type: "REMOVE_JOIN"; payload: string }
  | { type: "CLEAR_JOINS" }
  // Step 3: Fields
  | { type: "ADD_FIELD"; payload: ViewBuilderField }
  | { type: "REMOVE_FIELD"; payload: string }
  | {
      type: "UPDATE_FIELD";
      payload: { fieldId: string; updates: Partial<ViewBuilderField> };
    }
  | { type: "REORDER_FIELDS"; payload: ViewBuilderField[] }
  | { type: "SET_FIELDS"; payload: ViewBuilderField[] }
  // Step 4: Filters
  | { type: "ADD_FILTER"; payload: ViewBuilderFilter }
  | {
      type: "UPDATE_FILTER";
      payload: { filterId: string; updates: Partial<ViewBuilderFilter> };
    }
  | { type: "REMOVE_FILTER"; payload: string }
  | { type: "CLEAR_FILTERS" }
  // Step 5: Permissions
  | { type: "ADD_PERMISSION"; payload: ViewBuilderPermission }
  | {
      type: "UPDATE_PERMISSION";
      payload: {
        permissionId: string;
        updates: Partial<ViewBuilderPermission>;
      };
    }
  | { type: "REMOVE_PERMISSION"; payload: string }
  | { type: "SET_PERMISSIONS"; payload: ViewBuilderPermission[] }
  // Navigation
  | { type: "SET_STEP"; payload: number }
  // State
  | { type: "SET_DIRTY"; payload: boolean }
  | { type: "SET_SAVING"; payload: boolean }
  | { type: "SET_ERROR"; payload: string | null }
  // Validation
  | { type: "SET_VALIDATION_ERROR"; payload: { field: string; error: string } }
  | { type: "CLEAR_VALIDATION_ERROR"; payload: string }
  | { type: "CLEAR_ALL_VALIDATION_ERRORS" }
  | { type: "SET_VALIDATION_ERRORS"; payload: Record<string, string> };

// ============================================================================
// Initial State
// ============================================================================

const initialState: ViewBuilderState = {
  viewId: null,
  isEditing: false,
  name: "",
  description: "",
  focusTableId: null,
  focusTable: null,
  isTemplate: false,
  isPublic: false,
  relationships: [],
  selectedJoins: [],
  selectedFields: [],
  filters: [],
  permissions: [],
  currentStep: 0,
  isDirty: false,
  isSaving: false,
  error: null,
  validationErrors: {},
};

// ============================================================================
// Reducer
// ============================================================================

function reducer(
  state: ViewBuilderState,
  action: ViewBuilderAction,
): ViewBuilderState {
  switch (action.type) {
    case "LOAD_VIEW":
      return {
        ...state,
        ...action.payload,
        isDirty: false,
      };

    case "LOAD_RELATIONSHIPS":
      return {
        ...state,
        relationships: action.payload,
      };

    case "RESET":
      return initialState;

    // Step 1: Focus Table
    case "SET_NAME":
      return { ...state, name: action.payload, isDirty: true };

    case "SET_DESCRIPTION":
      return { ...state, description: action.payload, isDirty: true };

    case "SET_IS_TEMPLATE":
      return { ...state, isTemplate: action.payload, isDirty: true };

    case "SET_IS_PUBLIC":
      return { ...state, isPublic: action.payload, isDirty: true };

    case "SET_FOCUS_TABLE":
      return {
        ...state,
        focusTableId: action.payload.tableId,
        focusTable: action.payload.table,
        // Clear dependent data when focus table changes
        selectedJoins: [],
        selectedFields: [],
        filters: [],
        isDirty: true,
      };

    case "UPDATE_FOCUS_TABLE_COLUMNS":
      // Update focus table columns without clearing dependent data
      // Used when loading columns for an existing view in edit mode
      return {
        ...state,
        focusTable: state.focusTable
          ? { ...state.focusTable, columns: action.payload }
          : null,
      };

    case "CLEAR_FOCUS_TABLE":
      return {
        ...state,
        focusTableId: null,
        focusTable: null,
        selectedJoins: [],
        selectedFields: [],
        filters: [],
        isDirty: true,
      };

    // Step 2: Joins
    case "ADD_JOIN":
      return {
        ...state,
        selectedJoins: [...state.selectedJoins, action.payload],
        isDirty: true,
      };

    case "UPDATE_JOIN":
      return {
        ...state,
        selectedJoins: state.selectedJoins.map((j) =>
          j.id === action.payload.joinId
            ? { ...j, ...action.payload.updates }
            : j,
        ),
        isDirty: true,
      };

    case "REMOVE_JOIN": {
      // Cascade delete: remove this join and all child joins
      const joinToRemove = action.payload;
      const getAllChildJoinIds = (parentId: string): string[] => {
        const directChildren = state.selectedJoins
          .filter((j) => j.parentJoinId === parentId)
          .map((j) => j.id);
        const allDescendants = directChildren.flatMap((childId) =>
          getAllChildJoinIds(childId),
        );
        return [...directChildren, ...allDescendants];
      };
      const idsToRemove = new Set([
        joinToRemove,
        ...getAllChildJoinIds(joinToRemove),
      ]);

      // Also remove fields from removed joins
      const removedJoins = state.selectedJoins.filter((j) =>
        idsToRemove.has(j.id),
      );
      const removedAliases = new Set(removedJoins.map((j) => j.alias));

      return {
        ...state,
        selectedJoins: state.selectedJoins.filter(
          (j) => !idsToRemove.has(j.id),
        ),
        selectedFields: state.selectedFields.filter(
          (f) => f.tableAlias === null || !removedAliases.has(f.tableAlias),
        ),
        isDirty: true,
      };
    }

    case "CLEAR_JOINS":
      return {
        ...state,
        selectedJoins: [],
        // Also clear fields from joined tables
        selectedFields: state.selectedFields.filter(
          (f) => f.tableAlias === null,
        ),
        isDirty: true,
      };

    // Step 3: Fields
    case "ADD_FIELD":
      return {
        ...state,
        selectedFields: [...state.selectedFields, action.payload],
        isDirty: true,
      };

    case "REMOVE_FIELD":
      return {
        ...state,
        selectedFields: state.selectedFields.filter(
          (f) => f.id !== action.payload,
        ),
        isDirty: true,
      };

    case "UPDATE_FIELD":
      return {
        ...state,
        selectedFields: state.selectedFields.map((f) =>
          f.id === action.payload.fieldId
            ? { ...f, ...action.payload.updates }
            : f,
        ),
        isDirty: true,
      };

    case "REORDER_FIELDS":
      return {
        ...state,
        selectedFields: action.payload.map((f, idx) => ({
          ...f,
          sortOrder: idx,
        })),
        isDirty: true,
      };

    case "SET_FIELDS":
      return {
        ...state,
        selectedFields: action.payload,
        isDirty: true,
      };

    // Step 4: Filters
    case "ADD_FILTER":
      return {
        ...state,
        filters: [...state.filters, action.payload],
        isDirty: true,
      };

    case "UPDATE_FILTER":
      return {
        ...state,
        filters: state.filters.map((f) =>
          f.id === action.payload.filterId
            ? { ...f, ...action.payload.updates }
            : f,
        ),
        isDirty: true,
      };

    case "REMOVE_FILTER":
      return {
        ...state,
        filters: state.filters.filter((f) => f.id !== action.payload),
        isDirty: true,
      };

    case "CLEAR_FILTERS":
      return {
        ...state,
        filters: [],
        isDirty: true,
      };

    // Step 5: Permissions
    case "ADD_PERMISSION":
      return {
        ...state,
        permissions: [...state.permissions, action.payload],
        isDirty: true,
      };

    case "UPDATE_PERMISSION":
      return {
        ...state,
        permissions: state.permissions.map((p) =>
          p.id === action.payload.permissionId
            ? { ...p, ...action.payload.updates }
            : p,
        ),
        isDirty: true,
      };

    case "REMOVE_PERMISSION":
      return {
        ...state,
        permissions: state.permissions.filter((p) => p.id !== action.payload),
        isDirty: true,
      };

    case "SET_PERMISSIONS":
      return {
        ...state,
        permissions: action.payload,
        isDirty: true,
      };

    // Navigation
    case "SET_STEP":
      return { ...state, currentStep: action.payload };

    // State
    case "SET_DIRTY":
      return { ...state, isDirty: action.payload };

    case "SET_SAVING":
      return { ...state, isSaving: action.payload };

    case "SET_ERROR":
      return { ...state, error: action.payload };

    // Validation
    case "SET_VALIDATION_ERROR":
      return {
        ...state,
        validationErrors: {
          ...state.validationErrors,
          [action.payload.field]: action.payload.error,
        },
      };

    case "CLEAR_VALIDATION_ERROR": {
      const { [action.payload]: _, ...rest } = state.validationErrors;
      return { ...state, validationErrors: rest };
    }

    case "CLEAR_ALL_VALIDATION_ERRORS":
      return { ...state, validationErrors: {} };

    case "SET_VALIDATION_ERRORS":
      return { ...state, validationErrors: action.payload };

    default:
      return state;
  }
}

// ============================================================================
// Context Value Interface
// ============================================================================

interface ViewBuilderContextValue {
  state: ViewBuilderState;

  // Loading
  loadView: (data: Partial<ViewBuilderState>) => void;
  loadRelationships: (relationships: SchemaRelationshipResponse[]) => void;
  reset: () => void;

  // Step 1: Focus Table
  setName: (name: string) => void;
  setDescription: (description: string) => void;
  setIsTemplate: (isTemplate: boolean) => void;
  setIsPublic: (isPublic: boolean) => void;
  setFocusTable: (tableId: string, table: SchemaTableWithColumns) => void;
  clearFocusTable: () => void;
  updateFocusTableColumns: (columns: SchemaColumnResponse[]) => void;

  // Step 2: Joins
  addJoin: (
    relationship: SchemaRelationshipResponse,
    parentJoinId: string | null,
    joinType: JoinType,
    tables: SchemaTableWithColumns[],
  ) => void;
  updateJoin: (joinId: string, updates: Partial<ViewBuilderJoin>) => void;
  removeJoin: (joinId: string) => void;
  clearJoins: () => void;

  // Step 3: Fields
  addField: (
    column: SchemaColumnResponse,
    tableId: string,
    tableName: string,
    tableAlias: string | null,
  ) => void;
  removeField: (fieldId: string) => void;
  updateField: (fieldId: string, updates: Partial<ViewBuilderField>) => void;
  reorderFields: (fields: ViewBuilderField[]) => void;
  setFields: (fields: ViewBuilderField[]) => void;
  toggleField: (
    column: SchemaColumnResponse,
    tableId: string,
    tableName: string,
    tableAlias: string | null,
  ) => void;

  // Step 4: Filters
  addFilter: (
    columnId: string,
    tableAlias: string | null,
    operator: FilterOperator,
  ) => void;
  updateFilter: (filterId: string, updates: Partial<ViewBuilderFilter>) => void;
  removeFilter: (filterId: string) => void;
  clearFilters: () => void;

  // Step 5: Permissions
  addPermission: (groupId: string, groupName?: string) => void;
  updatePermission: (
    permissionId: string,
    updates: Partial<ViewBuilderPermission>,
  ) => void;
  removePermission: (permissionId: string) => void;
  setPermissions: (permissions: ViewBuilderPermission[]) => void;

  // Navigation
  setStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;

  // State
  setDirty: (isDirty: boolean) => void;
  setSaving: (isSaving: boolean) => void;
  setError: (error: string | null) => void;

  // Persistence
  loadDraft: () => boolean;
  clearDraft: () => void;
  hasDraft: () => boolean;
  getDraftInfo: () => { name: string; focusTableId: string | null } | null;

  // Validation
  setValidationError: (field: string, error: string) => void;
  clearValidationError: (field: string) => void;
  clearAllValidationErrors: () => void;
  validateCurrentStep: () => boolean;
  getFieldError: (field: string) => string | undefined;

  // Helpers
  generateAlias: (tableName: string, fkColumnName?: string) => string;
  getJoinById: (joinId: string) => ViewBuilderJoin | undefined;
  getFieldById: (fieldId: string) => ViewBuilderField | undefined;
  getAvailableRelationships: (tableId: string) => SchemaRelationshipResponse[];
  isFieldSelected: (columnId: string, tableAlias: string | null) => boolean;
}

const ViewBuilderContext = createContext<ViewBuilderContextValue | null>(null);

// ============================================================================
// Provider
// ============================================================================

interface ViewBuilderProviderProps {
  children: ReactNode;
}

export function ViewBuilderProvider({ children }: ViewBuilderProviderProps) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasLoadedDraftRef = useRef(false);

  // Persist to localStorage when state changes (debounced)
  useEffect(() => {
    // Don't save if we're editing an existing view
    if (state.isEditing) return;

    // Clear any existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Debounce the save
    saveTimeoutRef.current = setTimeout(() => {
      if (state.isDirty && !state.isSaving) {
        saveToStorage(state);
      }
    }, SAVE_DEBOUNCE_MS);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [
    state.name,
    state.description,
    state.focusTableId,
    state.currentStep,
    state.isTemplate,
    state.isPublic,
    state.selectedJoins,
    state.selectedFields,
    state.filters,
    state.permissions,
    state.isDirty,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    state.isSaving,
    state.isEditing,
  ]);

  // Loading
  const loadView = useCallback((data: Partial<ViewBuilderState>) => {
    dispatch({ type: "LOAD_VIEW", payload: data });
    // If loading an existing view, clear the draft
    if (data.isEditing) {
      clearStorage();
    }
  }, []);

  const loadRelationships = useCallback(
    (relationships: SchemaRelationshipResponse[]) => {
      dispatch({ type: "LOAD_RELATIONSHIPS", payload: relationships });
    },
    [],
  );

  const reset = useCallback(() => {
    dispatch({ type: "RESET" });
    clearStorage();
  }, []);

  // Persistence functions
  const clearDraft = useCallback(() => {
    clearStorage();
  }, []);

  const hasDraft = useCallback(() => {
    const draft = loadFromStorage();
    return draft !== null && (!!draft.name || !!draft.focusTableId);
  }, []);

  const getDraftInfo = useCallback(() => {
    const draft = loadFromStorage();
    if (!draft) return null;
    return { name: draft.name, focusTableId: draft.focusTableId };
  }, []);

  // Load draft on mount (only for new views)
  const loadDraft = useCallback(() => {
    if (hasLoadedDraftRef.current) return false;
    hasLoadedDraftRef.current = true;

    const draft = loadFromStorage();
    if (!draft || (!draft.name && !draft.focusTableId)) return false;

    dispatch({
      type: "LOAD_VIEW",
      payload: {
        name: draft.name,
        description: draft.description,
        focusTableId: draft.focusTableId,
        currentStep: draft.currentStep,
        isTemplate: draft.isTemplate ?? false,
        isPublic: draft.isPublic ?? false,
        selectedJoins: draft.selectedJoins ?? [],
        selectedFields: draft.selectedFields ?? [],
        filters: draft.filters ?? [],
        permissions: draft.permissions ?? [],
      },
    });
    return true;
  }, []);

  // Step 1: Focus Table
  const setName = useCallback((name: string) => {
    dispatch({ type: "SET_NAME", payload: name });
  }, []);

  const setDescription = useCallback((description: string) => {
    dispatch({ type: "SET_DESCRIPTION", payload: description });
  }, []);

  const setIsTemplate = useCallback((isTemplate: boolean) => {
    dispatch({ type: "SET_IS_TEMPLATE", payload: isTemplate });
  }, []);

  const setIsPublic = useCallback((isPublic: boolean) => {
    dispatch({ type: "SET_IS_PUBLIC", payload: isPublic });
  }, []);

  const setFocusTable = useCallback(
    (tableId: string, table: SchemaTableWithColumns) => {
      dispatch({ type: "SET_FOCUS_TABLE", payload: { tableId, table } });
    },
    [],
  );

  const clearFocusTable = useCallback(() => {
    dispatch({ type: "CLEAR_FOCUS_TABLE" });
  }, []);

  const updateFocusTableColumns = useCallback(
    (columns: SchemaColumnResponse[]) => {
      dispatch({ type: "UPDATE_FOCUS_TABLE_COLUMNS", payload: columns });
    },
    [],
  );

  // Helper: Generate unique alias using the _via_ pattern for clarity
  const generateAlias = useCallback(
    (tableName: string, fkColumnName?: string): string => {
      const existingAliases = state.selectedJoins.map((j) => j.alias);
      const sanitize = (s: string) =>
        s.toLowerCase().replace(/[^a-z0-9]/g, "_");

      // Use _via_{fkColumnName} pattern if available (more descriptive)
      let base: string;
      if (fkColumnName) {
        const tableBase = sanitize(tableName);
        const columnBase = sanitize(fkColumnName);
        base = `${tableBase}_via_${columnBase}`;
      } else {
        base = sanitize(tableName);
      }

      // Truncate if too long
      if (base.length > 50) base = base.substring(0, 50);

      if (!existingAliases.includes(base)) return base;

      // If alias exists, append counter
      let counter = 2;
      while (existingAliases.includes(`${base}_${counter}`)) counter++;
      return `${base}_${counter}`;
    },
    [state.selectedJoins],
  );

  // Step 2: Joins
  const addJoin = useCallback(
    (
      relationship: SchemaRelationshipResponse,
      parentJoinId: string | null,
      joinType: JoinType,
      tables: SchemaTableWithColumns[],
    ) => {
      // Determine source and target based on parent
      const sourceTableId = parentJoinId
        ? state.selectedJoins.find((j) => j.id === parentJoinId)
            ?.targetTableId || state.focusTableId
        : state.focusTableId;

      // The target table is the "other" side of the relationship
      const targetTableId =
        relationship.from_table_id === sourceTableId
          ? relationship.to_table_id
          : relationship.from_table_id;

      const sourceTable = tables.find((t) => t.id === sourceTableId);
      const targetTable = tables.find((t) => t.id === targetTableId);

      if (!sourceTable || !targetTable) return;

      // Find column names from the tables (use optional chaining since columns may not be loaded)
      const sourceColumn = sourceTable.columns?.find(
        (c) =>
          c.id ===
          (relationship.from_table_id === sourceTableId
            ? relationship.from_column_id
            : relationship.to_column_id),
      );
      const targetColumn = targetTable.columns?.find(
        (c) =>
          c.id ===
          (relationship.from_table_id === sourceTableId
            ? relationship.to_column_id
            : relationship.from_column_id),
      );

      // Generate alias using _via_ pattern: tableName_via_fkColumnName
      const fkColumnName = sourceColumn?.column_name;
      const alias = generateAlias(targetTable.table_name, fkColumnName);

      const newJoin: ViewBuilderJoin = {
        id: uuidv4(),
        relationshipId: relationship.id,
        relationship,
        parentJoinId,
        joinType,
        alias,
        joinOrder: state.selectedJoins.length,
        sourceTableName: sourceTable.display_name || sourceTable.table_name,
        sourceColumnName:
          sourceColumn?.display_name || sourceColumn?.column_name || "?",
        targetTableId,
        targetTableName: targetTable.display_name || targetTable.table_name,
        targetColumnName:
          targetColumn?.display_name || targetColumn?.column_name || "?",
      };

      dispatch({ type: "ADD_JOIN", payload: newJoin });
    },
    [state.focusTableId, state.selectedJoins, generateAlias],
  );

  const updateJoin = useCallback(
    (joinId: string, updates: Partial<ViewBuilderJoin>) => {
      dispatch({ type: "UPDATE_JOIN", payload: { joinId, updates } });
    },
    [],
  );

  const removeJoin = useCallback((joinId: string) => {
    dispatch({ type: "REMOVE_JOIN", payload: joinId });
  }, []);

  const clearJoins = useCallback(() => {
    dispatch({ type: "CLEAR_JOINS" });
  }, []);

  // Step 3: Fields
  const addField = useCallback(
    (
      column: SchemaColumnResponse,
      tableId: string,
      tableName: string,
      tableAlias: string | null,
    ) => {
      const newField: ViewBuilderField = {
        id: uuidv4(),
        columnId: column.id,
        column,
        tableId,
        tableName,
        tableAlias,
        displayLabel: column.display_name || column.column_name,
        isVisible: true,
        isEditable: tableAlias === null && !column.is_primary_key, // Only focus table fields are editable by default
        isLinkField: column.is_foreign_key,
        isUserIdentifier: false,
        sortOrder: state.selectedFields.length,
      };
      dispatch({ type: "ADD_FIELD", payload: newField });
    },
    [state.selectedFields.length],
  );

  const removeField = useCallback((fieldId: string) => {
    dispatch({ type: "REMOVE_FIELD", payload: fieldId });
  }, []);

  const updateField = useCallback(
    (fieldId: string, updates: Partial<ViewBuilderField>) => {
      dispatch({ type: "UPDATE_FIELD", payload: { fieldId, updates } });
    },
    [],
  );

  const reorderFields = useCallback((fields: ViewBuilderField[]) => {
    dispatch({ type: "REORDER_FIELDS", payload: fields });
  }, []);

  const setFields = useCallback((fields: ViewBuilderField[]) => {
    dispatch({ type: "SET_FIELDS", payload: fields });
  }, []);

  const toggleField = useCallback(
    (
      column: SchemaColumnResponse,
      tableId: string,
      tableName: string,
      tableAlias: string | null,
    ) => {
      const existing = state.selectedFields.find(
        (f) => f.columnId === column.id && f.tableAlias === tableAlias,
      );
      if (existing) {
        dispatch({ type: "REMOVE_FIELD", payload: existing.id });
      } else {
        const newField: ViewBuilderField = {
          id: uuidv4(),
          columnId: column.id,
          column,
          tableId,
          tableName,
          tableAlias,
          displayLabel: column.display_name || column.column_name,
          isVisible: true,
          isEditable: tableAlias === null && !column.is_primary_key,
          isLinkField: column.is_foreign_key,
          isUserIdentifier: false,
          sortOrder: state.selectedFields.length,
        };
        dispatch({ type: "ADD_FIELD", payload: newField });
      }
    },
    [state.selectedFields],
  );

  // Step 4: Filters
  const addFilter = useCallback(
    (columnId: string, tableAlias: string | null, operator: FilterOperator) => {
      const newFilter: ViewBuilderFilter = {
        id: uuidv4(),
        columnId,
        tableAlias,
        operator,
        value: null,
        isLocked: false,
      };
      dispatch({ type: "ADD_FILTER", payload: newFilter });
    },
    [],
  );

  const updateFilter = useCallback(
    (filterId: string, updates: Partial<ViewBuilderFilter>) => {
      dispatch({ type: "UPDATE_FILTER", payload: { filterId, updates } });
    },
    [],
  );

  const removeFilter = useCallback((filterId: string) => {
    dispatch({ type: "REMOVE_FILTER", payload: filterId });
  }, []);

  const clearFilters = useCallback(() => {
    dispatch({ type: "CLEAR_FILTERS" });
  }, []);

  // Step 5: Permissions
  const addPermission = useCallback((groupId: string, groupName?: string) => {
    const newPermission: ViewBuilderPermission = {
      id: uuidv4(),
      groupId,
      groupName,
      canView: true,
      canEdit: false,
      canDelete: false,
      canExport: false,
      rowScope: "own",
    };
    dispatch({ type: "ADD_PERMISSION", payload: newPermission });
  }, []);

  const updatePermission = useCallback(
    (permissionId: string, updates: Partial<ViewBuilderPermission>) => {
      dispatch({
        type: "UPDATE_PERMISSION",
        payload: { permissionId, updates },
      });
    },
    [],
  );

  const removePermission = useCallback((permissionId: string) => {
    dispatch({ type: "REMOVE_PERMISSION", payload: permissionId });
  }, []);

  const setPermissions = useCallback((permissions: ViewBuilderPermission[]) => {
    dispatch({ type: "SET_PERMISSIONS", payload: permissions });
  }, []);

  // Navigation
  const setStep = useCallback((step: number) => {
    dispatch({ type: "SET_STEP", payload: step });
  }, []);

  const nextStep = useCallback(() => {
    dispatch({ type: "SET_STEP", payload: Math.min(state.currentStep + 1, 4) });
  }, [state.currentStep]);

  const prevStep = useCallback(() => {
    dispatch({ type: "SET_STEP", payload: Math.max(state.currentStep - 1, 0) });
  }, [state.currentStep]);

  // State
  const setDirty = useCallback((isDirty: boolean) => {
    dispatch({ type: "SET_DIRTY", payload: isDirty });
  }, []);

  const setSaving = useCallback((isSaving: boolean) => {
    dispatch({ type: "SET_SAVING", payload: isSaving });
  }, []);

  const setError = useCallback((error: string | null) => {
    dispatch({ type: "SET_ERROR", payload: error });
  }, []);

  // Validation
  const setValidationError = useCallback((field: string, error: string) => {
    dispatch({ type: "SET_VALIDATION_ERROR", payload: { field, error } });
  }, []);

  const clearValidationError = useCallback((field: string) => {
    dispatch({ type: "CLEAR_VALIDATION_ERROR", payload: field });
  }, []);

  const clearAllValidationErrors = useCallback(() => {
    dispatch({ type: "CLEAR_ALL_VALIDATION_ERRORS" });
  }, []);

  const validateCurrentStep = useCallback(() => {
    const errors: Record<string, string> = {};

    switch (state.currentStep) {
      case 0: // Focus Table
        if (!state.name.trim()) {
          errors.name = "View name is required";
        }
        if (!state.focusTableId) {
          errors.focusTable = "Please select a focus table";
        }
        break;

      case 1: // Joins - optional, no required fields
        break;

      case 2: // Fields
        if (state.selectedFields.length === 0) {
          errors.fields = "Please select at least one field";
        }
        break;

      case 3: // Filters - optional, but validate configured filters
        state.filters.forEach((filter) => {
          if (
            filter.operator !== "is_null" &&
            filter.operator !== "is_not_null"
          ) {
            if (
              filter.value === null ||
              filter.value === undefined ||
              filter.value === ""
            ) {
              errors[`filter_${filter.id}`] = "Filter value is required";
            }
          }
        });
        break;

      case 4: // Permissions - if any permissions configured, at least one must have canView
        if (state.permissions.length > 0) {
          const hasViewPermission = state.permissions.some((p) => p.canView);
          if (!hasViewPermission) {
            errors.permissions = "At least one group must have View permission";
          }
        }
        break;
    }

    dispatch({ type: "SET_VALIDATION_ERRORS", payload: errors });
    return Object.keys(errors).length === 0;
  }, [
    state.currentStep,
    state.name,
    state.focusTableId,
    state.selectedFields,
    state.filters,
    state.permissions,
  ]);

  const getFieldError = useCallback(
    (field: string) => state.validationErrors[field],
    [state.validationErrors],
  );

  // Helpers
  const getJoinById = useCallback(
    (joinId: string) => state.selectedJoins.find((j) => j.id === joinId),
    [state.selectedJoins],
  );

  const getFieldById = useCallback(
    (fieldId: string) => state.selectedFields.find((f) => f.id === fieldId),
    [state.selectedFields],
  );

  const getAvailableRelationships = useCallback(
    (tableId: string) => {
      // Get relationships where this table is on either side
      // and that relationship is not already used
      const usedRelationshipIds = new Set(
        state.selectedJoins.map((j) => j.relationshipId),
      );
      return state.relationships.filter(
        (r) =>
          !usedRelationshipIds.has(r.id) &&
          (r.from_table_id === tableId || r.to_table_id === tableId),
      );
    },
    [state.relationships, state.selectedJoins],
  );

  const isFieldSelected = useCallback(
    (columnId: string, tableAlias: string | null) => {
      return state.selectedFields.some(
        (f) => f.columnId === columnId && f.tableAlias === tableAlias,
      );
    },
    [state.selectedFields],
  );

  const value: ViewBuilderContextValue = {
    state,
    loadView,
    loadRelationships,
    reset,
    setName,
    setDescription,
    setIsTemplate,
    setIsPublic,
    setFocusTable,
    clearFocusTable,
    updateFocusTableColumns,
    addJoin,
    updateJoin,
    removeJoin,
    clearJoins,
    addField,
    removeField,
    updateField,
    reorderFields,
    setFields,
    toggleField,
    addFilter,
    updateFilter,
    removeFilter,
    clearFilters,
    addPermission,
    updatePermission,
    removePermission,
    setPermissions,
    setStep,
    nextStep,
    prevStep,
    setDirty,
    setSaving,
    setError,
    loadDraft,
    clearDraft,
    hasDraft,
    getDraftInfo,
    setValidationError,
    clearValidationError,
    clearAllValidationErrors,
    validateCurrentStep,
    getFieldError,
    generateAlias,
    getJoinById,
    getFieldById,
    getAvailableRelationships,
    isFieldSelected,
  };

  return (
    <ViewBuilderContext.Provider value={value}>
      {children}
    </ViewBuilderContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

export function useViewBuilderContext() {
  const context = useContext(ViewBuilderContext);
  if (!context) {
    throw new Error(
      "useViewBuilderContext must be used within a ViewBuilderProvider",
    );
  }
  return context;
}

export default ViewBuilderContext;
